import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import FavouriteButton from "./FavouriteButton";

import partsIcon from "./assets/parts-icon.png";
import productIcon from "./assets/product-return.png";

const FittingDashboard = () => {
  const navigate = useNavigate();
  const auth = getAuth();

  const [roles, setRoles] = useState({}); // e.g. { admin:true, internal:true, production:true }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRoles({});
        setLoading(false);
        return;
      }
      try {
        const tokenRes = await user.getIdTokenResult(true);
        const claimRoles = tokenRes.claims?.roles;
        const map =
          claimRoles && typeof claimRoles === "object" && !Array.isArray(claimRoles)
            ? claimRoles
            : {};
        setRoles(map);
      } catch (e) {
        console.error("Failed to load token roles", e);
        setRoles({});
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, [auth]);

  // admin inherits all role-based tiles
  const can = (role) => !!(roles?.admin || roles?.[role]);

  const baseTiles = useMemo(() => ([
    { title: "QR Codes", icon: partsIcon, route: "/FittingShopQrList" },
  ]), []);

  const dynamicTiles = useMemo(() => {
    const tiles = [];
    // internal features (admin sees these too)
    if (can("fittingmanager")) {
      tiles.push({ title: "QR Uploader", icon: productIcon, route: "/FittingShopQrUpload" });
    }
    return tiles;
  }, [roles]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-sm text-gray-600">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-6">
      <FavouriteButton pageUrl={window.location.pathname} />
      <h1 className="text-4xl font-semibold text-gray-800 mb-8">Fitting Shop Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-5xl">
        {[...baseTiles, ...dynamicTiles].map((tile, i) => (
          <Tile key={`${tile.title}-${i}`} title={tile.title} icon={tile.icon} onClick={() => navigate(tile.route)} />
        ))}
      </div>
    </div>
  );
};

const Tile = ({ title, icon, onClick }) => (
  <div
    onClick={onClick}
    className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition cursor-pointer transform hover:-translate-y-1 flex flex-col items-center justify-center text-center w-full h-48 border border-gray-200"
  >
    <div className="flex justify-center items-center w-20 h-20 bg-gray-200 rounded-full group-hover:bg-primary transition">
      <img src={icon} alt={title} className="h-10 w-10 object-contain" />
    </div>
    <p className="mt-4 text-lg font-semibold text-gray-800 group-hover:text-primary transition">{title}</p>
  </div>
);

export default FittingDashboard;
